import { AsyncSelectInputOption } from '@/components/Forms/AsyncSelect';
import {
  emptyPayeeTransactionValues,
  hasSelectedPayee,
  PayeeOrganizationSelect,
  PayeeTransactionFields,
  PayeeTransactionFormValues,
  payeeTransactionSchema,
  toCreatePayeeTransactionPayload,
} from '@/components/Payouts/admin/payeeTransactionForm';
import { useCreatePayeeTransactionMutation } from '@/services/payouts';
import { Alert, Box, Button, Modal, toast } from '@palmetto/palmetto-components';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';

/**
 * Staged the way the project transaction modal stages its account id: the rest of the form appears
 * only once an organization is chosen.
 */
const AddPayeeTransactionFormFields = () => {
  const { values } = useFormikContext<PayeeTransactionFormValues>();

  return (
    <Box childGap={{ base: 'lg', desktop: 'xl' }}>
      <PayeeOrganizationSelect />
      {hasSelectedPayee(values) && <PayeeTransactionFields />}
    </Box>
  );
};

interface AddPayeeTransactionModalProps {
  isOpen: boolean;
  handleClose: () => void;
  modalId: string;
  /** Prefilled when the tab is filtered to a single organization. */
  defaultPayee?: AsyncSelectInputOption;
}

export const AddPayeeTransactionModal = ({
  isOpen,
  handleClose,
  modalId,
  defaultPayee,
}: AddPayeeTransactionModalProps) => {
  const [createPayeeTransaction] = useCreatePayeeTransactionMutation();
  const [submitError, setSubmitError] = useState<string>();

  const initialValues: PayeeTransactionFormValues = useMemo(
    () => ({ payee: defaultPayee ?? '', ...emptyPayeeTransactionValues }),
    [defaultPayee],
  );

  const closeAndReset = useCallback(
    (resetForm: () => void) => {
      setSubmitError(undefined);
      resetForm();
      handleClose();
    },
    [handleClose],
  );

  const onSubmit = useCallback(
    async (
      values: PayeeTransactionFormValues,
      { setSubmitting, resetForm }: FormikHelpers<PayeeTransactionFormValues>,
    ) => {
      setSubmitError(undefined);
      try {
        await createPayeeTransaction(toCreatePayeeTransactionPayload(values)).unwrap();
        toast.success('Transaction added successfully');
        resetForm();
        handleClose();
      } catch (error: any) {
        // Shown in place rather than as a toast, and the form is left untouched, so a rejected
        // amount or organization can be corrected without retyping the rest.
        setSubmitError(error?.data?.message ?? 'Could not add transaction');
      } finally {
        setSubmitting(false);
      }
    },
    [createPayeeTransaction, handleClose],
  );

  return (
    <Formik
      validationSchema={payeeTransactionSchema}
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnBlur
    >
      {({ isSubmitting, resetForm, submitForm }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            maxWidth="4xl"
            ariaLabelledBy={modalId}
            onDismiss={() => {
              if (!isSubmitting) closeAndReset(resetForm);
            }}
          >
            <Modal.Header
              id={modalId}
              title="Add Organization Transaction"
              onDismiss={() => {
                if (!isSubmitting) closeAndReset(resetForm);
              }}
            />
            <Modal.Body background="secondary" childGap="lg">
              {submitError && <Alert variant="danger" message={submitError} hasIcon />}
              <AddPayeeTransactionFormFields />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                // A button inside a form defaults to submit, so without this Cancel resets the form
                // and then submits it, leaving validation errors behind on the way out.
                type="button"
                onClick={() => closeAndReset(resetForm)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                type="submit"
                onClick={submitForm}
              >
                Add Transaction
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
